import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge, Button, Spinner, Alert, Row, Col, Table } from 'react-bootstrap';
import { FaSync, FaTrash, FaCheckCircle, FaTimesCircle, FaClock, FaCog } from 'react-icons/fa';
import { vexaCompilationService } from '../../services/vexaCompilationService';

const STATUS_CONFIG = {
  NOT_COMPILED: { icon: '⚪', label: 'Sin compilar', color: 'secondary' },
  PROCESSING: { icon: '🟡', label: 'Compilando...', color: 'warning' },
  READY: { icon: '🟢', label: 'Compilado', color: 'success' },
  FAILED: { icon: '🔴', label: 'Error', color: 'danger' },
  SKIPPED: { icon: '🟢', label: 'Sin cambios', color: 'info' },
};

export default function VexaCompilationPanel({ manualId, module, screen, content, version = '1.0.0' }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [health, setHealth] = useState(null);

  const getStatusIndicator = useCallback(() => {
    if (!status) return STATUS_CONFIG.NOT_COMPILED;
    return STATUS_CONFIG[status.status] || STATUS_CONFIG.NOT_COMPILED;
  }, [status]);

  const handleCompile = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await vexaCompilationService.compileManual(manualId, version, { content, module, screen });
      setStatus({ status: 'PROCESSING', ...result });

      // Poll until complete
      await vexaCompilationService.pollStatus(manualId, (update) => {
        setStatus(update);
      });
    } catch (err) {
      setError(err.message);
      setStatus({ status: 'FAILED' });
    } finally {
      setLoading(false);
    }
  };

  const handleRecompile = async () => {
    setLoading(true);
    setError('');
    try {
      await vexaCompilationService.recompileManual(manualId, version);
      await handleCompile();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setError('');
    try {
      await vexaCompilationService.deleteManual(manualId);
      setStatus(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStatus = async () => {
    try {
      const s = await vexaCompilationService.getStatus(manualId);
      setStatus(s);
    } catch {
      setStatus(null);
    }
  };

  const loadHealth = async () => {
    try {
      const h = await vexaCompilationService.health();
      setHealth(h);
    } catch {
      setHealth(null);
    }
  };

  useEffect(() => {
    if (manualId) loadStatus();
  }, [manualId]);

  const indicator = getStatusIndicator();

  return (
    <Card className="shadow-sm border-0">
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="mb-0 d-flex align-items-center gap-2">
            <FaCog /> Compilación Vexa
          </h6>
          <div className="d-flex align-items-center gap-2">
            <Badge bg={indicator.color} className="d-flex align-items-center gap-1 px-3 py-2">
              {indicator.icon} {indicator.label}
            </Badge>
            {status?.status === 'PROCESSING' && <Spinner size="sm" />}
          </div>
        </div>

        {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

        {status && (
          <Row className="mb-3 g-2 small">
            <Col xs={6}>
              <strong>Estado:</strong> {status.status}
            </Col>
            <Col xs={6}>
              <strong>Etapa:</strong> {status.currentStage || '-'}
            </Col>
            <Col xs={6}>
              <strong>Chunks:</strong> {status.chunks ?? '-'}
            </Col>
            <Col xs={6}>
              <strong>Embeddings:</strong> {status.embeddings ?? '-'}
            </Col>
            <Col xs={6}>
              <strong>Calidad:</strong> {status.qualityScore != null ? `${status.qualityScore}/100` : '-'}
            </Col>
            <Col xs={6}>
              <strong>Versión:</strong> {status.version || version}
            </Col>
          </Row>
        )}

        <div className="d-flex gap-2">
          <Button
            size="sm"
            variant="primary"
            onClick={handleCompile}
            disabled={loading || !manualId}
          >
            {loading ? <Spinner size="sm" /> : <FaSync className="me-1" />}
            Compilar
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={handleRecompile}
            disabled={loading || !status}
          >
            Recompilar
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={handleDelete}
            disabled={loading || !status}
          >
            <FaTrash className="me-1" /> Eliminar
          </Button>
          <Button
            size="sm"
            variant="outline-info"
            onClick={loadHealth}
          >
            Health
          </Button>
        </div>

        {health && (
          <div className="mt-3 small">
            <strong>Health:</strong>
            <Badge bg={health.healthy ? 'success' : 'danger'} className="ms-2">
              {health.healthy ? 'OK' : 'ERROR'}
            </Badge>
            <Table size="sm" className="mt-2 mb-0">
              <tbody>
                {Object.entries(health).filter(([k]) => k !== 'timestamp').map(([k, v]) => (
                  <tr key={k}>
                    <td className="text-capitalize">{k}</td>
                    <td>
                      <Badge bg={v?.status === 'ok' || v === true ? 'success' : 'warning'}>
                        {v?.status || (v ? 'ok' : 'error')}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
